import styled from '@emotion/styled'
import type { TFunction } from 'i18next'

import { ArcadeButton, spacing, ThemeVariantProvider } from '@cash-design-system/react'

import type { LinkAdapterProps } from '../../types/types'
import { Content } from '../styles'
import SupportLogo from '../SupportLogo'

const Container = styled.header`
  display: flex;
  align-items: center;
  padding-top: ${spacing.l};
  padding-bottom: ${spacing.smallGap};
  box-shadow: ${props => props.theme.dropShadows.topBar};
  z-index: 100;
`

const StyledContent = styled(Content)`
  margin: auto;
  max-width: 1680px;
  display: flex;
  justify-content: space-between;
`

const SignUpAndLoginContainer = styled.div`
  display: flex;
  gap: ${spacing.s};
`

const ButtonContent = styled.span`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  /* modifiers to match marketing sign up and login buttons */
  ${props => props.theme.typography.identifier};
  font-weight: 700; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
`

const SvgContainer = styled.span`
  padding-left: ${spacing.xs};
`

// TODO: janec, this is not an Arcade Icon but it matches Marketing's Header
const SignUpButtonArrow = () => (
  <SvgContainer>
    <svg width="11" height="15" viewBox="0 -4 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.82276 0.500619L11.7722 5.45007C12.0759 5.75379 12.0759 6.24622 11.7722 6.54994L6.82276 11.4994C6.51904 11.8031 6.0266 11.8031 5.72288 11.4994C5.41916 11.1957 5.41916 10.7032 5.72288 10.3995L9.34466 6.77774H0.777731C0.348202 6.77774 0 6.42954 0 6.00001C0 5.57048 0.348202 5.22228 0.777731 5.22228H9.34466L5.72288 1.6005C5.41916 1.29677 5.41916 0.804342 5.72288 0.500619C6.0266 0.196896 6.51904 0.196896 6.82276 0.500619Z"
        fill="currentColor"
      />
    </svg>
  </SvgContainer>
)

interface HeaderProps<T> extends LinkAdapterProps<T> {
  /** I18next translation function*/ t: TFunction
  isLoggedIn?: boolean
}

const LinkButton = styled(ArcadeButton)<{ href: string }>`
  text-decoration: none;
`

const SignUpArcadeButton = styled(LinkButton)`
  display: none;
  ${({ theme }) => theme.breakpoints.tablet} {
    display: block;
  }
`

const Header = <T,>({ t, isLoggedIn = false, ...props }: HeaderProps<T>) => {
  return (
    <ThemeVariantProvider themeVariant="monochrome">
      <Container>
        <StyledContent>
          <SupportLogo Link={props.Link} linkProps={props.linkProps} />
          {isLoggedIn ? (
            <LinkButton prominence="standard" size="default" as="a" href="/account">
              <ButtonContent>
                <span>{t('Account', { context: 'link to accounts text' })}</span>
              </ButtonContent>
            </LinkButton>
          ) : (
            <SignUpAndLoginContainer>
              <SignUpArcadeButton prominence="prominent" size={'default'} as="a" href="/login?su&redirect=support">
                <ButtonContent>
                  <span>{t('Sign up', { context: 'link to sign up text' })}</span>
                  <SignUpButtonArrow />
                </ButtonContent>
              </SignUpArcadeButton>
              <LinkButton prominence="standard" size="default" as="a" href="/login?redirect=support">
                <ButtonContent>
                  <span>{t('Log in', { context: 'link to log in text' })}</span>
                </ButtonContent>
              </LinkButton>
            </SignUpAndLoginContainer>
          )}
        </StyledContent>
      </Container>
    </ThemeVariantProvider>
  )
}

export default Header
